import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { Unit } from '@/api/generated/models';
import { getListUnitsQueryKey, useCreateUnit, useUpdateUnit } from '@/api/generated/units/units';
import { FormModal, ModalFooter, MutationButton, TextField, Toast } from '@/components/common';
import { SUPPORTED_LANGUAGES } from '@/i18n/supportedLanguages';

const namesSchema = z.object(
  Object.fromEntries(SUPPORTED_LANGUAGES.map((lang) => [lang.code, z.string().min(1)]))
);

const schema = z.object({
  code: z.string().min(1),
  names: namesSchema
});

type FormValues = z.infer<typeof schema>;

const emptyNames = Object.fromEntries(SUPPORTED_LANGUAGES.map((lang) => [lang.code, '']));

const emptyValues: FormValues = {
  code: '',
  names: emptyNames
};

// Isti obrazac kao ObjectFormModal/OfferLineFormModal — backend već vraća
// specifičan, lokalizirani razlog ako nešto ne uspije.
const unitErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface UnitFormModalProps {
  opened: boolean;
  onClose: () => void;
  unit?: Unit;
  existingCodes?: string[];
  onSaved?: (message: string) => void;
}

/**
 * Jedna forma za kreiranje I uređivanje jedinice (Figma: "Nova jedinica" dialog,
 * isti izgled se ponovno koristi za "Uredi jedinicu" — nema odvojenog edit
 * screena u dizajnu). NAZIV polja se generiraju iz SUPPORTED_LANGUAGES —
 * dodavanje novog jezika u aplikaciju automatski doda novo obavezno polje
 * ovdje, bez ikakve promjene koda. Deaktiviraj/Aktiviraj/Obriši su namjerno
 * IZVAN ove forme — žive u UnitActionsMenu na listi (isti obrazac kao
 * ObjectRegistryPage: status-mijenjajuće akcije uz redak, ne u edit formi).
 */
export const UnitFormModal = ({
  opened,
  onClose,
  unit,
  existingCodes = [],
  onSaved
}: UnitFormModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!unit;

  const codeSchema = schema.superRefine((data, ctx) => {
    if (!isEdit && existingCodes.includes(data.code.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['code'],
        message: t('adminUnits.codeDuplicate')
      });
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(codeSchema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues(
      unit
        ? {
            code: unit.code ?? '',
            names: { ...emptyNames, ...unit.names }
          }
        : emptyValues
    );
  }, [opened, unit]);

  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: getListUnitsQueryKey() });
  };

  const createMutation = useCreateUnit({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateList();
        onSaved?.(t('adminUnits.createSuccess'));
        onClose();
      }
    }
  });

  const updateMutation = useUpdateUnit({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateList();
        onSaved?.(t('adminUnits.updateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && unit?.id) {
      updateMutation.mutate({ id: unit.id, data: { names: values.names } });
    } else {
      createMutation.mutate({ data: { code: values.code, names: values.names } });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? t('adminUnits.editTitle') : t('adminUnits.newTitle')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          {mutation.isError && (
            <Toast tone="danger">{unitErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <TextField
            label={t('adminUnits.fieldCode')}
            required
            disabled={isEdit}
            placeholder={t('adminUnits.codePlaceholder')}
            {...form.getInputProps('code')}
          />

          {SUPPORTED_LANGUAGES.map((lang) => (
            <TextField
              key={lang.code}
              label={`${t('adminUnits.fieldNamePrefix')} — ${lang.label}`}
              required
              placeholder={t('adminUnits.namePlaceholder')}
              {...form.getInputProps(`names.${lang.code}`)}
            />
          ))}

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('adminUnits.saveButton')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
